import React from 'react';
import { Star, ChevronLeft, ChevronRight, Shuffle, Sparkles, MapPin, Quote } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { AppConfig, Gallery, MediaItem } from '../types';
import { EditableText } from './EditableText';

interface FeaturedMasterpieceProps {
  galleries?: Gallery[];
  imageUrl?: string;
  quote?: string;
  author?: string;
  isAdmin?: boolean;
  isVisualEditActive?: boolean;
  onUpdateConfig?: (partialConfig: Partial<AppConfig>) => void;
  onToggleFavoriteMedia?: (galleryId: string, mediaId: string) => void;
}

interface FavoriteMediaItem {
  media: MediaItem;
  galleryId: string;
  galleryTitle: string;
  galleryLocation: string;
}

export function FeaturedMasterpiece({ 
  galleries = [], 
  quote, 
  author, 
  isVisualEditActive = false,
  onUpdateConfig,
  onToggleFavoriteMedia
}: FeaturedMasterpieceProps) {
  const { lang, t } = useLanguage();

  // 1. Collect all media marked as favorite ⭐ across all galleries
  const favoriteItems = React.useMemo(() => {
    const list: FavoriteMediaItem[] = [];
    galleries.forEach(gallery => {
      gallery.media.forEach(m => {
        if (m.isFavorite) {
          list.push({
            media: m,
            galleryId: gallery.id,
            galleryTitle: gallery.title,
            galleryLocation: gallery.location
          });
        }
      });
    });
    return list;
  }, [galleries]);

  // 2. Fallback to all media if no favorite is checked yet
  const displayItems = React.useMemo(() => {
    if (favoriteItems.length > 0) return favoriteItems;

    // Fallback: collect all media items or cover photos
    const fallbackList: FavoriteMediaItem[] = [];
    galleries.forEach(gallery => {
      gallery.media.forEach(m => {
        fallbackList.push({
          media: m,
          galleryId: gallery.id,
          galleryTitle: gallery.title,
          galleryLocation: gallery.location
        });
      });
    });

    // Default static placeholder if galleries are completely empty
    if (fallbackList.length === 0) {
      fallbackList.push({
        media: {
          id: 'default-masterpiece',
          type: 'photo',
          url: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1200&q=80',
          alt: "Chef-d'œuvre d'exception",
          caption: "Instant magique et poétique",
          isFavorite: true
        },
        galleryId: 'demo',
        galleryTitle: "Mariage d'Exception",
        galleryLocation: "Bassin d'Arcachon"
      });
    }

    return fallbackList;
  }, [favoriteItems, galleries]);

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);

  // Keep index within bounds
  React.useEffect(() => {
    if (currentIndex >= displayItems.length) {
      setCurrentIndex(0);
    }
  }, [displayItems.length, currentIndex]);

  // Auto rotation every 12 seconds (slow, contemplative pace)
  React.useEffect(() => {
    if (isPaused || displayItems.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % displayItems.length);
    }, 12000);
    return () => clearInterval(timer);
  }, [isPaused, displayItems.length]);

  const currentItem = displayItems[currentIndex] || displayItems[0];

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % displayItems.length);
  };

  const handlePrev = () => {
    setCurrentIndex(prev => (prev === 0 ? displayItems.length - 1 : prev - 1));
  };

  const handleShuffle = () => {
    if (displayItems.length <= 1) return;
    let nextIdx = Math.floor(Math.random() * displayItems.length);
    if (nextIdx === currentIndex) {
      nextIdx = (currentIndex + 1) % displayItems.length;
    }
    setCurrentIndex(nextIdx);
  };

  const isFavoriteCount = favoriteItems.length;

  return (
    <section className="py-24 bg-theme-card relative overflow-hidden border-t border-theme-border z-10">
      {/* Soft Background Glares */}
      <div className="absolute top-1/4 left-10 w-72 h-72 rounded-full bg-theme-accent/5 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-10 w-80 h-80 rounded-full bg-theme-border/15 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Text Info Column */}
          <div className="lg:col-span-5 space-y-6">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.25em] text-theme-accent font-mono block">
                {t('master_tag')}
              </span>
              {isFavoriteCount > 0 && (
                <span className="px-2 py-0.5 bg-amber-400/10 border border-amber-400/30 text-amber-300 font-mono text-[10px] rounded-full flex items-center gap-1 font-bold">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  {isFavoriteCount} {lang === 'fr' ? 'Sélectionné(s)' : 'Starred'}
                </span>
              )}
            </div>

            <h2 className="text-3xl md:text-5xl font-serif text-theme-text font-light tracking-wide leading-tight">
              {t('master_title')}
            </h2>
            <div className="w-12 h-[1px] bg-theme-accent/50" />
            
            <p className="text-sm text-theme-secondary font-light leading-relaxed">
              {t('master_desc')}
            </p>
            
            <div className="text-xs text-theme-secondary/80 font-mono italic leading-relaxed bg-theme-bg/40 p-4 border-l-2 border-theme-accent space-y-2 relative">
              <Quote className="w-5 h-5 text-theme-accent/20 absolute top-2 right-2 pointer-events-none" />
              <EditableText
                value={quote || t('master_quote_fallback')}
                onSave={(val) => onUpdateConfig?.({ masterpieceQuote: val })}
                isEditingMode={isVisualEditActive}
                label="Citation Chef-d'œuvre"
                multiline
              >
                <span>"{quote || t('master_quote_fallback')}"</span>
              </EditableText>
              <br />
              <EditableText
                value={author || t('master_author_fallback')}
                onSave={(val) => onUpdateConfig?.({ masterpieceAuthor: val })}
                isEditingMode={isVisualEditActive}
                label="Auteur de la citation"
              >
                <span className="text-[10px] text-theme-secondary/60 not-italic block mt-1">— {author || t('master_author_fallback')}</span>
              </EditableText>
            </div>


          </div>

          {/* Masterpiece Photo Showcase Frame */}
          <div className="lg:col-span-7 flex justify-center items-center min-h-[420px] md:min-h-[500px]">
            <div
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              className={`relative w-full ${
                currentItem.media.aspectRatio === '3:4' ? 'aspect-[3/4] max-w-md' :
                currentItem.media.aspectRatio === '4:3' ? 'aspect-[4/3] max-w-xl' :
                currentItem.media.aspectRatio === '1:1' ? 'aspect-square max-w-lg' :
                'aspect-[16/9] max-w-xl'
              } bg-theme-bg/40 border border-theme-border/60 p-3 shadow-2xl flex flex-col group overflow-hidden transition-all duration-700 ease-out`}
            >
              {/* Image Canvas with Pristine Clean Transition */}
              <div className="relative w-full h-full overflow-hidden bg-black/95 rounded-sm">
                <AnimatePresence mode="sync">
                  <motion.div
                    key={currentItem.media.id + '-' + currentIndex}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden"
                  >
                    {/* Soft Blurred Background Fill to Prevent Any Empty Black Bar Disconnect */}
                    {currentItem.media.type !== 'video' && (
                      <img
                        src={currentItem.media.url}
                        alt=""
                        referrerPolicy="no-referrer"
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-40 scale-125 pointer-events-none"
                      />
                    )}

                    {/* Crisp Full Image Display (No Crop) */}
                    {currentItem.media.type === 'video' ? (
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-contain relative z-10"
                      >
                        {currentItem.media.h265Url && currentItem.media.h265Url.trim() !== '' && (
                          <source src={currentItem.media.h265Url} type='video/mp4; codecs="hevc, mp4a.40.2"' />
                        )}
                        {currentItem.media.h264Url && currentItem.media.h264Url.trim() !== '' && (
                          <source src={currentItem.media.h264Url} type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
                        )}
                        {currentItem.media.url && currentItem.media.url.trim() !== '' && (
                          <source src={currentItem.media.url} type="video/mp4" />
                        )}
                      </video>
                    ) : (
                      <img
                        src={currentItem.media.url}
                        alt={currentItem.media.alt || "Chef-d'œuvre"}
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-contain relative z-10 select-none transform group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                      />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Aesthetic Corner Accents */}
              <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-theme-accent/60" />
              <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-theme-accent/60" />
              <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-theme-accent/60" />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-theme-accent/60" />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
